import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, Tag, Space, Select, Dropdown } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import dayjs from "dayjs";
import api from "../../api";
import BannerFormDrawer from "./BannerFormDrawer";
import { IconArrowRight } from "@tabler/icons-react";
import ConfirmModal from "../../components/modal/ConfirmModal";
import {
  BANNER_STATUS_COLOR,
  BANNER_STATUS_OPTIONS,
  PLACEMENT_LABEL,
  PLACEMENT_OPTIONS,
} from "../../constants";

const PAGE_SIZE = 15;

export default function Banners() {
  const toast = useToast();

  // --- State ---
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [placement, setPlacement] = useState(null);
  const [status, setStatus] = useState(null);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  // --- Fetch ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: PAGE_SIZE };
    if (placement) params.placement = placement;
    if (status) params.status = status;

    const res = await api.banners_list(params);
    if (res.status === 200) {
      setData(res.data.data);
      setTotal(res.data.total);
    } else {
      toast(res?.data?.message || "Өгөгдөл татахад алдаа гарлаа", "error");
    }

    setLoading(false);
  }, [page, placement, status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Handlers ---
  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditTarget(row);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    if (editTarget) {
      const res = await api.banner_update(editTarget.id, values);
      if (res.status === 200) {
        toast("Амжилттай засагдлаа", "success");
      } else {
        toast(res?.data?.message || "Алдаа гарлаа", "error");
      }
    } else {
      const res = await api.banners_create(values);
      if (res.status === 201) {
        toast("Амжилттай нэмэгдлээ", "success");
      } else {
        toast(res?.data?.message || "Алдаа гарлаа", "error");
      }
    }
    closeModal();
    fetchData();
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    const res = await api.banner_delete(id);
    if (res.status === 200) {
      toast("Устгагдлаа", "success");
      setDeleteModal({ open: false, id: null });
      fetchData();
    } else {
      toast("Устгахад алдаа гарлаа", "error");
    }
  };

  // --- Columns ---
  const columns = [
    {
      title: "Зураг",
      dataIndex: "imageUrl",
      width: 120,
      render: (url) =>
        url ? (
          <img
            src={url}
            style={{
              objectFit: "cover",
              borderRadius: 4,
              width: "100%",
              aspectRatio: 16 / 9,
            }}
          />
        ) : (
          <div
            style={{
              width: 60,
              height: 40,
              borderRadius: 4,
              background: "var(--gray-100)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: "var(--gray-400)",
            }}
          >
            Зураггүй
          </div>
        ),
    },
    {
      title: "Гарчиг",
      dataIndex: "title",
      render: (title, row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          {row.subtitle && (
            <div style={{ fontSize: 12, color: "var(--gray-500)" }}>
              {row.subtitle}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Байршил",
      dataIndex: "placement",
      render: (p) => <Tag>{PLACEMENT_LABEL[p] ?? p}</Tag>,
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      render: (s) => (
        <Tag color={BANNER_STATUS_COLOR(s)}>
          {s === "PUBLISHED" ? "Нийтлэгдсэн" : "Ноорог"}
        </Tag>
      ),
    },
    {
      title: "Огноо",
      render: (_, row) => {
        if (!row.startsAt && !row.endsAt)
          return <span style={{ color: "var(--gray-400)" }}>Хязгааргүй</span>;
        return (
          <div style={{ fontSize: 12 }}>
            <div>
              {row.startsAt ? dayjs(row.startsAt).format("YYYY/MM/DD") : "—"}
            </div>
            <div>
              {row.endsAt ? dayjs(row.endsAt).format("YYYY/MM/DD") : "—"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Дараалал",
      dataIndex: "sortOrder",
      width: 80,
      align: "center",
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
              if (key === "delete")
                setDeleteModal({ open: true, id: record.id });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <section className="banners-section">
      <PageHeader
        title="Баннер"
        sub="Hero, slider болон жагсаалтын дээрх баннерууд"
      />

      {/* Toolbar */}
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <Space wrap>
          <Select
            allowClear
            placeholder="Байршил"
            style={{ width: 200 }}
            options={PLACEMENT_OPTIONS}
            value={placement}
            onChange={(v) => {
              setPlacement(v);
              setPage(1);
            }}
          />
          <Select
            allowClear
            placeholder="Төлөв"
            style={{ width: 150 }}
            options={BANNER_STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          />
        </Space>

        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Баннер нэмэх
        </Button>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={{ spinning: loading, size: "small" }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            showTotal: (t) => `Нийт ${t}`,
          }}
        />
      </div>

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal({ open: v, id: null })}
        submitHandler={() => handleDelete(deleteModal.id)}
        title="Итгэлтэй байна уу?"
        description={
          <>
            <span style={{ fontWeight: 600 }}>"Баннер устгах"</span> үйлдлийг
            буцаах боломжгүйг анхаарна уу.
          </>
        }
        variant="error"
      />

      {/* DRAWER */}
      <BannerFormDrawer
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initial={editTarget}
        loading={submitting}
      />
    </section>
  );
}
